import React from 'react';

import { bemCssModules } from '@cryptoboyio/mercuryo-lab/utils';
import { useTranslation } from 'react-i18next';

import { useIsMobile, useNotifications, useLocation, useNavigate } from '@utils/hooks';
import { LocationStateType } from '@utils/types';

import { userActions, useStore } from '@modules';
import { ProtectNextEnum } from '@modules/types';
import { EditUserParams } from '@modules/user/types';

import StrongPasswordModal from '@containers/StrongPasswordModal';
import LoaderLayout from '@layouts/LoaderLayout';
import WalletLayout from '@layouts/WalletLayout';

import AccountForm from './AccountForm';

import style from './ProfileEdit.scss';

const cn = bemCssModules(style, 'ProfileEdit');
const { verifyOTP, setupOTP } = ProtectNextEnum;

const ProfileEdit: CustomFC = ({ className }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { successEditSettingsNotification } = useNotifications();
  const [{ user }, dispatch] = useStore();
  const isMobile = useIsMobile();
  const location = useLocation<LocationStateType['profileEdit']>();
  const { state } = location;

  const onChangeValue = (field: keyof EditUserParams, value: string | boolean) => {
    if (field === 'strong_security' && typeof value === 'boolean') {
      dispatch(userActions.toggleStrongSecurity({ strong_security: value }))
        .unwrap()
        .then(res => {
          if (res.data.next === setupOTP) {
            navigate(CONST.nav.intro2fa);
          } else if (res.data.next === verifyOTP) {
            navigate<'verifications'>(CONST.nav.verification, {
              state: {
                nextPage: CONST.nav.profile,
                flow: 'ToggleStrongSecurityOTP',
                strongPassword: value,
              },
            });
          } else {
            successEditSettingsNotification(field, value);
          }
        })
        .catch(err => {
          if (err.data.next === setupOTP) {
            navigate(CONST.nav.intro2fa);
          } else if (err.data.next === verifyOTP) {
            navigate<'verifications'>(CONST.nav.verification, {
              state: {
                nextPage: CONST.nav.profile,
                flow: 'ToggleStrongSecurityOTP',
                strongPassword: value,
              },
            });
          }
          return err;
        });
    } else {
      dispatch(userActions.editUser({ [field]: value }))
        .unwrap()
        .then(() => {
          successEditSettingsNotification(field, value);
        });
    }
  };

  const isLoading = user.loaders.getUserData && !user?.data;

  const onBack = () => navigate(state?.from || CONST.nav.profile);

  return (
    <WalletLayout
      header={<div className={cn('header')}>{t('account.personal_data')}</div>}
      backButton={onBack}
      hasSidebar={!isMobile}
    >
      <div className={cn(null, [className])}>
        <LoaderLayout isLoading={isLoading}>
          <AccountForm onChangeValue={onChangeValue} />
        </LoaderLayout>
      </div>
      <StrongPasswordModal />
    </WalletLayout>
  );
};

export default ProfileEdit;
